import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiSuccess } from '@alamo/types';

import { apiClient } from '../../client';
import type {
  PaymentBankCatalogResponseDto,
  PaymentKycDocumentSlotDto,
  PaymentOnboardingActionCountResponseDto,
  PaymentOnboardingResponseDto,
  ResubmitPaymentKycDocumentsDto,
  StartPaymentKycDocumentsResponseDto,
  SubmitPaymentOnboardingDto,
  SubmitPaymentOnboardingResponseDto,
  UpdatePaymentOnboardingDraftDtoWritable,
  ValidatePaymentOnboardingResponseDto,
} from '../../generated/types.gen';

export const paymentOnboardingKeys = {
  all: ['payment-onboarding'] as const,
  own: () => [...paymentOnboardingKeys.all, 'own'] as const,
  actionCount: () => [...paymentOnboardingKeys.all, 'action-count'] as const,
  banks: () => [...paymentOnboardingKeys.all, 'banks'] as const,
};

export function usePaymentOnboarding(enabled = true) {
  return useQuery({
    queryKey: paymentOnboardingKeys.own(),
    queryFn: async () => {
      const response =
        await apiClient.get<ApiSuccess<PaymentOnboardingResponseDto>>('/me/payment-onboarding');
      return response.data.data;
    },
    enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'provisioning' || status === 'submitting' ? 5_000 : false;
    },
  });
}

export function usePaymentOnboardingActionCount(enabled = true) {
  return useQuery({
    queryKey: paymentOnboardingKeys.actionCount(),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<PaymentOnboardingActionCountResponseDto>>(
        '/me/payment-onboarding/action-count',
      );
      return response.data.data;
    },
    enabled,
    staleTime: 30_000,
  });
}

export function usePaymentBankCatalog() {
  return useQuery({
    queryKey: paymentOnboardingKeys.banks(),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<PaymentBankCatalogResponseDto>>(
        '/me/payment-onboarding/banks',
      );
      return response.data.data;
    },
    staleTime: 24 * 60 * 60 * 1_000,
  });
}

function useInvalidatePaymentOnboarding() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: paymentOnboardingKeys.own() }),
      queryClient.invalidateQueries({ queryKey: paymentOnboardingKeys.actionCount() }),
    ]);
  };
}

export function useUpdatePaymentOnboardingDraft() {
  const invalidate = useInvalidatePaymentOnboarding();
  return useMutation({
    mutationFn: async (body: UpdatePaymentOnboardingDraftDtoWritable) => {
      const response = await apiClient.patch<ApiSuccess<PaymentOnboardingResponseDto>>(
        '/me/payment-onboarding/draft',
        body,
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useValidatePaymentOnboarding() {
  const invalidate = useInvalidatePaymentOnboarding();
  return useMutation({
    mutationFn: async (draftVersion: number) => {
      const response = await apiClient.post<ApiSuccess<ValidatePaymentOnboardingResponseDto>>(
        '/me/payment-onboarding/validate',
        { draftVersion },
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useStartPaymentDocuments() {
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<ApiSuccess<StartPaymentKycDocumentsResponseDto>>(
        '/me/payment-onboarding/documents/start',
      );
      return response.data.data;
    },
  });
}

export function useUploadPaymentDocument() {
  const invalidate = useInvalidatePaymentOnboarding();
  return useMutation({
    mutationFn: async (input: {
      kind: string;
      file: File;
      uploadIntent: string;
    }): Promise<PaymentKycDocumentSlotDto> => {
      const form = new FormData();
      form.append('file', input.file);
      const response = await apiClient.post<ApiSuccess<PaymentKycDocumentSlotDto>>(
        `/me/payment-onboarding/documents/${encodeURIComponent(input.kind)}`,
        form,
        {
          headers: {
            'Content-Type': undefined,
            'x-upload-intent': input.uploadIntent,
          },
          timeout: 90_000,
        },
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useDeletePaymentDocument() {
  const invalidate = useInvalidatePaymentOnboarding();
  return useMutation({
    mutationFn: async (attachmentId: string): Promise<void> => {
      await apiClient.delete(
        `/me/payment-onboarding/documents/${encodeURIComponent(attachmentId)}`,
      );
    },
    onSuccess: invalidate,
  });
}

export function useSubmitPaymentOnboarding() {
  const invalidate = useInvalidatePaymentOnboarding();
  return useMutation({
    mutationFn: async (input: { body: SubmitPaymentOnboardingDto; idempotencyKey: string }) => {
      const response = await apiClient.post<ApiSuccess<SubmitPaymentOnboardingResponseDto>>(
        '/me/payment-onboarding/submit',
        input.body,
        { headers: { 'idempotency-key': input.idempotencyKey } },
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useResubmitPaymentDocuments() {
  const invalidate = useInvalidatePaymentOnboarding();
  return useMutation({
    mutationFn: async (input: { body: ResubmitPaymentKycDocumentsDto; idempotencyKey: string }) => {
      const response = await apiClient.post<ApiSuccess<SubmitPaymentOnboardingResponseDto>>(
        '/me/payment-onboarding/documents/resubmit',
        input.body,
        { headers: { 'idempotency-key': input.idempotencyKey } },
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}
